/**
 * Collection Gap Analysis
 *
 * Given a user's owned markers for a brand, finds which unowned markers would
 * add the most new color coverage — using a greedy set-cover approach in
 * CIELAB color space with CIEDE2000 distance.
 */

import { hexToRgb, rgbToLab, deltaE2000, analyzeColor } from './color';

/**
 * Convert a hex string to a LAB object, cached per hex for performance.
 */
const labCache = new Map();
function hexToLab(hex) {
  if (labCache.has(hex)) return labCache.get(hex);
  const { r, g, b } = hexToRgb(hex);
  const lab = rgbToLab(r, g, b);
  labCache.set(hex, lab);
  return lab;
}

/**
 * Minimum distance from a candidate hex to any marker in a reference set.
 */
function minDistanceTo(candidateHex, referenceMarkers) {
  if (!referenceMarkers.length) return Infinity;
  const cLab = hexToLab(candidateHex);
  let min = Infinity;
  for (const m of referenceMarkers) {
    const d = deltaE2000(cLab, hexToLab(m.hex));
    if (d < min) min = d;
  }
  return min;
}

/**
 * Derive a human-friendly reason label for a suggested marker.
 * Based on what hue/tone area it covers that's currently sparse.
 */
function gapReasonLabel(hex, ownedMarkers) {
  const analysis = analyzeColor(hex);

  // Tone-based labels take priority for very dark/light markers
  if (analysis.isDark) return 'deep shadow tone';
  if (analysis.isVeryLight) return 'soft highlight tone';

  if (analysis.isNeutral) {
    // Check if user already has neutrals
    const hasNeutral = ownedMarkers.some(m => analyzeColor(m.hex).isNeutral);
    return hasNeutral ? 'additional neutral' : 'adds a versatile neutral';
  }

  const hueLabels = {
    red:    'fills your reds',
    orange: 'fills your oranges',
    yellow: 'fills your yellows',
    green:  'fills your greens',
    cyan:   'fills your teals',
    blue:   'fills your blues',
    purple: 'fills your purples',
  };

  const label = hueLabels[analysis.dominantHue];
  if (label) {
    // Check if user already has some of this hue
    const hasHue = ownedMarkers.some(
      m => analyzeColor(m.hex).dominantHue === analysis.dominantHue
    );
    if (hasHue) {
      return `expands your ${analysis.dominantHue}s`;
    }
    return label;
  }

  return 'expands your coverage';
}

/**
 * Main gap analysis function.
 *
 * @param {Array} owned    - Markers the user already has (same brand)
 * @param {Array} unowned  - Remaining markers of that brand not in collection
 * @param {number} count   - How many suggestions to return (1–20)
 * @returns {Array<{ marker, gain, reason }>}
 *   - marker: the suggested marker object
 *   - gain: deltaE distance from nearest owned color (higher = more new coverage)
 *   - reason: human-friendly label
 */
export function findCollectionGaps(owned, unowned, count = 5) {
  if (!owned || !unowned || unowned.length === 0) return [];

  const clampedCount = Math.max(1, Math.min(count, unowned.length));

  // Filter out markers with no hex
  const candidates = unowned.filter(m => m.hex && m.hex !== '#');
  const virtualOwned = [...owned];
  const suggestions = [];

  for (let i = 0; i < clampedCount; i++) {
    let best = null;
    let bestGain = -1;

    for (const candidate of candidates) {
      const gain = minDistanceTo(candidate.hex, virtualOwned);
      if (gain > bestGain) {
        bestGain = gain;
        best = candidate;
      }
    }

    if (!best) break;

    suggestions.push({
      marker: best,
      gain: bestGain,
      reason: gapReasonLabel(best.hex, virtualOwned),
    });

    virtualOwned.push(best);
    candidates.splice(candidates.indexOf(best), 1);
  }

  return suggestions;
}

/**
 * Returns per-brand stats for the user's collection vs. full brand data.
 * Used to populate the brand selector on the gap analysis page.
 *
 * @param {Array} userCollection  - All markers in user's collection
 * @param {Array} allMarkers      - Full brand marker database
 * @param {string[]} brands       - Brand names to include (exclude "All", "My Collection")
 * @returns {Array<{ brand, owned, total, unownedCount }>}
 */
export function getBrandCoverageStats(userCollection, allMarkers, brands) {
  return brands
    .filter(b => b !== 'All' && b !== 'My Collection')
    .map(brand => {
      const brandMarkers = allMarkers.filter(m => m.brand === brand && m.code !== '0' && m.code !== 0);
      const ownedSet = new Set(
        userCollection
          .filter(m => m.brand === brand)
          .map(m => (brand === 'Ohuhu' ? m.hex?.toLowerCase() : String(m.code || '').toUpperCase()))
      );
      const owned = brandMarkers.filter(m => {
        const key = brand === 'Ohuhu' ? m.hex?.toLowerCase() : String(m.code || '').toUpperCase();
        return ownedSet.has(key);
      }).length;

      return {
        brand,
        owned,
        total: brandMarkers.length,
        unownedCount: brandMarkers.length - owned,
      };
    })
    .filter(s => s.total > 0 && s.owned > 0); // Only brands user partially owns
}

/**
 * Returns the owned and unowned marker arrays for a specific brand,
 * ready to pass to findCollectionGaps.
 *
 * @param {Array} userCollection
 * @param {Array} allMarkers
 * @param {string} brand
 * @returns {{ owned: Array, unowned: Array }}
 */
export function splitOwnedUnowned(userCollection, allMarkers, brand) {
  const brandMarkers = allMarkers.filter(
    m => m.brand === brand && m.code !== '0' && m.code !== 0
  );

  const isOwned = (marker) => {
    return userCollection.some(m => {
      if (m.brand !== brand) return false;
      if (brand === 'Ohuhu') {
        return m.hex?.toLowerCase() === marker.hex?.toLowerCase();
      }
      return String(m.code || '').toUpperCase() === String(marker.code || '').toUpperCase();
    });
  };

  const owned = brandMarkers.filter(isOwned);
  const unowned = brandMarkers.filter(m => !isOwned(m));

  return { owned, unowned };
}
